import { NewTag } from "@/design-system/Tags/NewTag/NewTag";
import { cn } from "@/lib/utils";
import { useTranslation } from "@repo/shared/i18n";
import { Link } from "react-router";

import { SiteNavPage, SiteNavSection } from "./siteNavConfig";

/** A page in a mega panel: title, tag and one-line description. */
export const NavPageTile = ({
  page,
  isCurrent,
  onNavigate,
}: {
  page: SiteNavPage;
  isCurrent: boolean;
  onNavigate: () => void;
}) => {
  const { t } = useTranslation("common");
  return (
    <Link
      to={page.href}
      onClick={onNavigate}
      aria-current={isCurrent ? "page" : undefined}
      className="flex flex-col gap-y-0.5 rounded-lg px-3 py-2.5 transition-colors hover:bg-surface"
    >
      <span className="flex items-center gap-x-2">
        <span className="text-body-lg text-foreground">{page.label}</span>
        {page.tag && <NewTag tag={t(`tags.${page.tag}`)} />}
      </span>
      <span className="text-small text-foreground-muted">
        {page.description}
      </span>
    </Link>
  );
};

/** The head of a mega panel. */
export const NavSectionTitle = ({ section }: { section: SiteNavSection }) => (
  <span className="text-h2 text-foreground">{section.label}</span>
);

/** Section group in the site map: a plain heading over its pages. */
export const NavDirectoryGroup = ({
  section,
  onNavigate,
  subtle,
  className,
}: {
  section: SiteNavSection;
  onNavigate: () => void;
  /** Secondary info rather than tools: heading and links a step dimmer. */
  subtle?: boolean;
  className?: string;
}) => {
  const { t } = useTranslation("common");
  return (
    <div className={cn("flex flex-col gap-y-1", className)}>
      {section.headless ? (
        // Stands in for a heading: same type, but a link and never dimmed.
        <Link
          to={section.href}
          onClick={onNavigate}
          className={`px-2.5 py-1 font-oswald text-body tracking-wide uppercase hover:text-foreground ${section.pages[0].highlight ? "text-primary-blue" : "text-foreground"}`}
        >
          {section.pages[0].label}
        </Link>
      ) : (
        <span
          className={`px-2.5 py-1 font-oswald text-body tracking-wide uppercase ${subtle ? "text-foreground-muted" : "text-foreground"}`}
        >
          {section.label}
        </span>
      )}
      {/* text-body sits here: next to a text colour, cn() would merge it away. */}
      {!section.headless && (
        <div className="flex flex-col text-body">
          {section.pages.map((page) => (
            <Link
              key={page.key}
              to={page.href}
              onClick={onNavigate}
              className={cn(
                "flex h-8 items-center gap-x-2 rounded-lg px-2.5 transition-colors hover:bg-surface hover:text-foreground",
                page.highlight
                  ? "text-primary-blue"
                  : subtle
                    ? "text-foreground-faint"
                    : "text-foreground-muted",
              )}
            >
              {page.label}
              {page.tag && <NewTag tag={t(`tags.${page.tag}`)} />}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};
